import { Image } from 'plotly.js'
import { toImage } from 'plotly.js/dist/plotly-axon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ScatterPlotMetadata } from 'redux/slices'
import { ColumnTable } from 'arquero'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useElementById from 'utils/useElementById'
import { useMonitoring } from '../../../services/monitoring'
import { HIDDEN_COLOR } from '../shared'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import getMarkerSize from './getMarkerSize'
import { usePlotData } from './usePlotData'
import { getMetadataFromDataFrame } from './utils'

const buffer = 5

const getImage = (
  imgUrl: string,
  position: { x: number; y: number },
  size: { height: number; width: number }
) => {
  return {
    x: position.x,
    y: position.y,
    xref: 'x',
    yref: 'y',
    xanchor: 'center',
    yanchor: 'middle',
    sizex: size.width,
    sizey: size.height,
    sizing: 'stretch',
    source: imgUrl,
    layer: 'below',
  } as Partial<Image>
}

type GetImageUrlParams = {
  scatterPlotMetaData: ScatterPlotMetadata
  /**
   * Default to a big enough image so that when the user zooms in the image doesn't get too grainy
   */
  width?: number
  height?: number
  markerSize?: number
  dataFrame?: ColumnTable
  prefilteredCellIdSet: Set<string>
  isPreFilter: boolean
}

const getImageUrl = async (
  {
    scatterPlotMetaData,
    width = 2000,
    height = 2000,
    markerSize = 1,
    dataFrame,
    prefilteredCellIdSet,
    isPreFilter,
  }: GetImageUrlParams,
  onError?: (error: unknown) => void
): Promise<string | null> => {
  const { maxX, maxY, minX, minY, x, y } = scatterPlotMetaData

  // Extract visible cell coordinates from DataFrame (memory-efficient)
  let visibleX: number[] | undefined
  let visibleY: number[] | undefined

  if (!isPreFilter && dataFrame) {
    // Extract columns directly from DataFrame (zero-copy)
    const cellIdCol = dataFrame.column('CELL_ID')
    const umapXCol = dataFrame.column('UMAP_0')
    const umapYCol = dataFrame.column('UMAP_1')

    if (cellIdCol && cellIdCol.toArray && umapXCol && umapXCol.toArray && umapYCol && umapYCol.toArray) {
      const cellIdColumn = cellIdCol.toArray() as string[]
      const umapXColumn = umapXCol.toArray() as number[]
      const umapYColumn = umapYCol.toArray() as number[]

      // Filter out prefiltered cells
      const visibleXArray: number[] = []
      const visibleYArray: number[] = []
      for (let i = 0; i < cellIdColumn.length; i += 1) {
        if (!prefilteredCellIdSet.has(cellIdColumn[i])) {
          visibleXArray.push(umapXColumn[i])
          visibleYArray.push(umapYColumn[i])
        }
      }
      visibleX = visibleXArray
      visibleY = visibleYArray
    }
  }

  try {
    const url = await toImage(
      {
        data: [
          {
            x: isPreFilter ? x : visibleX,
            y: isPreFilter ? y : visibleY,
            type: 'scattergl',
            mode: 'markers',
            marker: {
              color: HIDDEN_COLOR,
              size: markerSize,
            },
          },
        ],
        layout: {
          margin: { t: 0, b: 0, l: 0, r: 0, pad: 0 },
          xaxis: {
            visible: false,
            range: [minX - buffer, maxX + buffer],
          },
          yaxis: {
            visible: false,
            range: [minY - buffer, maxY + buffer],
          },
          showlegend: false,
        },
      },
      { scale: 1, format: 'png', width, height }
    )
    return url
  } catch (error) {
    console.error('Error generating image URL:', error)
    // In case of error, log to monitoring as well
    if (onError) {
      onError(error)
    }
    return null
  }
}

export const useHiddenBackgroundImages = (): Partial<Image>[] => {
  const {
    dataFrame,
    visibleSelectedComparisonDimensions,
    cellVisualizations: { morphotypesToRemove },
    prefilteredCellIdSet,
  } = useCellVisualizationsSlice()
  const { isPreFilter } = useCellVisualizationUrlParams()
  const { scatterPlotMetaData } = usePlotData()
  const monitoring = useMonitoring()

  // Use DataFrame directly for metadata calculation (memory-efficient)
  const filteredScatterPlotMetaData = useMemo(() => {
    if (!dataFrame) {
      return {
        minX: 0,
        minY: 0,
        maxX: 0,
        maxY: 0,
        x: [],
        y: [],
        cellIds: [],
        plotDatum: [],
      }
    }
    return getMetadataFromDataFrame(dataFrame, morphotypesToRemove)
  }, [dataFrame, morphotypesToRemove])

  const comparisonDimensionsKey = useMemo(
    () => visibleSelectedComparisonDimensions?.map(dim => dim.field).join(','),
    [visibleSelectedComparisonDimensions]
  );

  const metaData = useMemo(() => {
    return isPreFilter || visibleSelectedComparisonDimensions?.length
      ? scatterPlotMetaData
      : filteredScatterPlotMetaData
  }, [
    isPreFilter,
    visibleSelectedComparisonDimensions,
    scatterPlotMetaData,
    filteredScatterPlotMetaData,
  ])

  const { rect } = useElementById('scatter-plot')

  const [images, setImages] = useState<Partial<Image>[]>([])

  const plotDimensions = useMemo(() => {
    const { maxX, minX, maxY, minY } = metaData
    return {
      plotWidth: maxX - minX,
      plotHeight: maxY - minY,
      centerX: (maxX + minX) / 2,
      centerY: (maxY + minY) / 2,
      xOffset: (maxX - minX) * 1.5,
      yOffset: (maxY - minY) * 1.5,
    }
  }, [metaData])

  const getImageSize = useCallback(
    () => ({
      width: plotDimensions.plotWidth + buffer * 2,
      height: plotDimensions.plotHeight + buffer * 2,
    }),
    [plotDimensions.plotHeight, plotDimensions.plotWidth]
  )

  const getImages = useCallback(
    (imgUrl: string) => {
      const img = [] as Partial<Image>[]
      if (visibleSelectedComparisonDimensions) {
        const numOfColumns = visibleSelectedComparisonDimensions[0].values.length
        const totalCount =
          (visibleSelectedComparisonDimensions[1]?.values.length || 1) * numOfColumns
        for (let i = 0; i < totalCount; i += 1) {
          const col = i % numOfColumns
          const row = Math.floor(i / numOfColumns)
          img.push(
            getImage(
              imgUrl,
              {
                x: plotDimensions.centerX + plotDimensions.xOffset * col,
                y: plotDimensions.centerY - plotDimensions.yOffset * row,
              },
              getImageSize()
            )
          )
        }
      } else {
        img.push(
          getImage(imgUrl, { x: plotDimensions.centerX, y: plotDimensions.centerY }, getImageSize())
        )
      }

      return img
    },
    [
      plotDimensions.centerX,
      plotDimensions.centerY,
      getImageSize,
      visibleSelectedComparisonDimensions,
      plotDimensions.xOffset,
      plotDimensions.yOffset,
    ]
  )

  useEffect(() => {
    const loadImages = async () => {
      if (!metaData.x.length) return

      const markerSize = getMarkerSize(metaData.x.length)
      const { height, width } = rect ?? {}
      const url = await getImageUrl(
        {
          scatterPlotMetaData: metaData,
          width,
          height,
          markerSize,
          dataFrame,
          prefilteredCellIdSet,
          isPreFilter,
        },
        (error) => monitoring.logError(error as Error)
      )
      if (url) {
        const img = getImages(url)
        if (img.length) {
          setImages(img)
        }
      }
    }
    loadImages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataFrame?.numRows,
    morphotypesToRemove,
    prefilteredCellIdSet.size,
    comparisonDimensionsKey,
    rect?.width,
    rect?.height,
    isPreFilter,
    metaData,
    getImages
  ])

  return images
}

export default useHiddenBackgroundImages
